<script setup lang="ts">
import {apps, LegacyDelivery, LegacyOrder, legacyOrderKey, LegacyOrderRow} from '@hantera/portal-app';
import {inject, ref, useTemplateRef, watch} from 'vue';
import OrderRowSummary from './OrderRowSummary.vue';
import OrderRowAdjustments from './OrderRowAdjustments.vue';
import OrderRowProduct from './OrderRowProduct.vue';
import AppSlotView from '@/AppSlotView.vue';
import {IconChevron} from "@hantera/design-system";

const props = defineProps<{
  delivery: LegacyDelivery
  row: LegacyOrderRow
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));

const expanded = ref(false);
const detailsStyle = ref<any>({});
const details = useTemplateRef<HTMLDivElement>('details');

function toggleExpand() {
  expanded.value = !expanded.value;
}

let styleTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
watch(expanded, v => {

  if (!v) {
    clearTimeout(styleTimeout);
    detailsStyle.value = {
      maxHeight: details.value?.scrollHeight + 'px',
    };
    styleTimeout = setTimeout(() => {
      detailsStyle.value = {};
    }, 0);
    return;
  }

  detailsStyle.value = {
    maxHeight: details.value?.scrollHeight + 'px',
  };

  styleTimeout = setTimeout(() => {
    detailsStyle.value = {
      maxHeight: 'none',
    };
  }, 100);

});

</script>

<template>
  <div
    class="legacy-order-row box-content"
    :class="{ 'legacy-order-row--expanded': expanded }"
  >
    <div class="legacy-order-row-primary p">
      <OrderRowProduct class="legacy-order-row__product" :order-row="row" :delivery="delivery" />
      <OrderRowSummary class="legacy-order-row__summary" :row="row" :delivery="delivery" />
  
      <div class="legacy-order-row-expand-button" @click.prevent.stop="toggleExpand">
        <IconChevron class="legacy-order-row-expand-button-arrow" />
      </div>
    </div>

    <div
      class="legacy-order-row-details"
      ref="details"
      :style="detailsStyle"
    >
      <div class="legacy-order-row-details-margin">
        <div class="legacy-order-row__extras">
          <AppSlotView
            :slot="apps.legacySlots.legacyOrder.delivery.rowDetails"
            :context="{
              delivery,
              orderRow: row
            }"
          />
        </div>
    
        <OrderRowAdjustments :order-row="row" />
      </div>
    </div>
  </div>
</template>

<style>
.legacy-order-row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  grid-auto-rows: max-content;
  padding: 0;
}


.legacy-order-row__extras {
  grid-column: 1 / middle;
  border-right: 1px solid hsl(var(--color-70));
  height: 100%;
}

.legacy-order-row-primary {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.legacy-order-row-expand-button {
  padding: 5px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
  grid-column: -2;
  grid-row: 1 / 3;
}
.legacy-order-row-expand-button-arrow {
  width: 14px;
}
.legacy-order-row--expanded .legacy-order-row-expand-button-arrow {
  transform: rotate(180deg);
}

.legacy-order-row-details {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;

  overflow: hidden;
  transition: max-height 0.1s ease-out;
  max-height: 0;
  box-sizing: border-box;
}

.legacy-order-row-details-margin {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  grid-template-rows: auto;
  border-top: 1px solid hsl(var(--color-70));
  align-items: start;
}

@container (max-width: 767px) {
  .legacy-order-row {
    display: grid;
    grid-auto-rows: max-content;
    padding: 0;
    margin-bottom: 5px;
  }
  
  .legacy-order-row-expand-button {
    grid-row: 1 / 3;
  }


    .legacy-order-row-adjustments {
      grid-row: 1 / 2;
      grid-column: 1 / -2;
    }
  
  .legacy-order-row__extras {
    border-right: none;
    border-top: 1px solid hsl(var(--color-70));
    grid-row: 2 / 3;
    grid-column: 1 / -1;
  }
}

</style>